import React from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { uploadData } from "aws-amplify/storage";
import { CategoryDefaultIcon } from "../components/StudioErstellen/EquipmentOptionalCategories";

const CATEGORY_SLUGS = {
    Sonstiges: "sonstiges",
    "Aufnahme & Mikrofone": "aufnahme-mikrofone",
    "Lautsprecher & Kopfhörer (Monitoring)": "lautsprecher-kopfhoerer",
    "Recorder, Software & Computer": "recorder-software-computer",
    "Mischpulte & Steuerung": "mischpulte-steuerung",
    "Klangbearbeitung (Preamps & Effekte)": "klangbearbeitung",
    Instrumente: "instrumente",
    "Akustik & Kabel": "akustik-kabel",
    Lichtequipment: "lichtequipment",
    "Kameras und Objektive": "kameras-objektive",
    Stative: "stative",
    "Set-Accessoires": "set-accessoires",
};

export function categoryToDefaultImageSlug(category) {
    return CATEGORY_SLUGS[category] || CATEGORY_SLUGS.Sonstiges;
}

export function isCategoryDefaultImageKey(imageKey) {
    return typeof imageKey === "string" && imageKey.includes("paid-equipment-defaults/");
}

/** SVG markup for S3 (same icon as UI CategoryDefaultIcon). */
export function getCategoryDefaultSvgString(category) {
    const markup = renderToStaticMarkup(
        <CategoryDefaultIcon category={category || "Sonstiges"} />
    );
    return markup
        .replace(/\swidth="40"/, ' width="400"')
        .replace(/\sheight="40"/, ' height="400"');
}

/** Rasterize category icon so CloudFront resize/WebP works like other equipment photos. */
export async function categoryDefaultSvgToPngBlob(category) {
    const svg = getCategoryDefaultSvgString(category);
    const svgUrl = URL.createObjectURL(new Blob([svg], { type: "image/svg+xml" }));

    try {
        const img = await new Promise((resolve, reject) => {
            const el = new Image();
            el.onload = () => resolve(el);
            el.onerror = () => reject(new Error("Category default icon failed to load"));
            el.src = svgUrl;
        });

        const size = 400;
        const canvas = document.createElement("canvas");
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
            throw new Error("Canvas not supported");
        }
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, size, size);
        ctx.drawImage(img, 0, 0, size, size);

        const blob = await new Promise((resolve, reject) => {
            canvas.toBlob(
                (b) => (b ? resolve(b) : reject(new Error("PNG export failed"))),
                "image/png"
            );
        });
        return blob;
    } finally {
        URL.revokeObjectURL(svgUrl);
    }
}

export async function uploadCategoryDefaultImage(category) {
    const slug = categoryToDefaultImageSlug(category);
    // Do not prefix `public/` — Amplify adds it for defaultAccessLevel public.
    const storageKey = `paid-equipment-defaults/${slug}.png`;
    const pngBlob = await categoryDefaultSvgToPngBlob(category);
    const { key } = await uploadData({
        key: storageKey,
        data: pngBlob,
        options: { contentType: "image/png" },
    }).result;
    return key;
}

/**
 * Resolves imageKey for create/update: user upload, keep custom key, or category default SVG.
 * @param {object} item - paid equipment row from wizard or editor
 * @param {(image: { file: File }) => Promise<string>} uploadUserImage
 */
export async function resolvePaidEquipmentItemImageKey(item, uploadUserImage) {
    const imageFile = item?.image?.file;
    if (imageFile instanceof File) {
        return uploadUserImage(item.image);
    }

    const imageKey = item?.imageKey || null;

    if (imageKey && item?.defaultThumbnailFromCategory === false) {
        return imageKey;
    }

    if (imageKey && !isCategoryDefaultImageKey(imageKey) && item?.defaultThumbnailFromCategory !== true) {
        return imageKey;
    }

    return uploadCategoryDefaultImage(item?.category || "Sonstiges");
}
